import type {
  BaseEntity,
  Role,
  UserDetail,
  UserDetailPayload,
} from '~settings/api/lib/schemas';
import { fromDropdownList, toDropdownList } from '~settings/utils/transformers';

import type { UserFormSchema, UserFormSchemaForExternalUser } from '../schema';

export const mapFormDataToApi = (
  data: UserFormSchema | UserFormSchemaForExternalUser,
): UserDetailPayload => {
  const payload: UserDetailPayload = {
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone?.length ? data.phone : null,
    jobTitle: data.jobTitle,
    isInternal: data.userType === 'internal',
    organization: '',
    rolesMap: {},
  };

  if (data.userType === 'internal') {
    payload.organization = data.organization;

    for (const role of data.internalPermissions.roles) {
      payload.rolesMap[role.value as Role] = {
        labels: [],
        labelGroups: fromDropdownList(data.internalPermissions.labelGroups),
      };
    }
  } else {
    for (const { role, labels } of data.externalPermissions) {
      const existingRole = payload.rolesMap[role as Role];

      if (existingRole) {
        const uniqueLabels = labels.filter(
          (label) =>
            !existingRole.labels.find(
              (storedLabel: BaseEntity) => storedLabel.id === label.value,
            ),
        );

        payload.rolesMap[role as Role] = {
          labels: [...existingRole.labels, ...fromDropdownList(uniqueLabels)],
          labelGroups: [],
        };
      } else {
        payload.rolesMap[role as Role] = {
          labels: fromDropdownList(labels),
          labelGroups: [],
        };
      }
    }
  }

  return payload;
};

export const mapApiDataToForm = (user: UserDetail): UserFormSchema => {
  const rolesMap = user.rolesMap ?? {};

  if (user.isInternal) {
    const userRoles = Object.keys(rolesMap) as Role[];
    const firstRole = rolesMap[userRoles[0]]; // they are the same for all internal roles
    const labelGroups: UserDetail['labelGroups'] = firstRole?.labelGroups ?? [];

    return {
      externalPermissions: [],
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      jobTitle: user.jobTitle,
      phone: user.phone ?? '',
      organization: user.organization ?? '',
      userType: 'internal',
      internalPermissions: {
        roles: userRoles.map((role) => ({
          label: role,
          value: role,
        })),
        labelGroups: toDropdownList(labelGroups) ?? [],
      },
    };
  }

  const externalPermissions = Object.entries(rolesMap).map(
    ([role, entries]) => {
      return {
        role: role as Role,
        labels: toDropdownList(entries.labels) ?? [],
      };
    },
  );

  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    jobTitle: user.jobTitle,
    phone: user.phone ?? '',
    userType: 'external',
    externalPermissions,
  };
};
